// KidWatchTab — the kid's live cook-watching view. Subscribes to
// cook_session_participants for this kid's user_id and surfaces any
// ACTIVE cook session they're a participant on (typically as diner).
// Renders a big recipe-title card with the live current step the
// lead is on (synced via cook_session_steps realtime), the rooting
// emoji strip, and a soft "nothing's cooking" empty state.
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useEffect, useMemo, useState } from "react";
import { COLOR, FONT } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";
import ReactionsStrip from "../../components/ReactionsStrip";

export function KidWatchTab({ userId }) {
  const [session, setSession] = useState(null);
  const [recipe, setRecipe] = useState(null);
  // openSteps — map of cook_session_steps row id → row. Each entry is
  // one helper's currently-open step. Multi-cook means N entries
  // simultaneously (Marissa chopping + Trevor searing → 2 cards).
  // Map shape lets the realtime tail patch a single row without
  // re-sorting the whole array.
  const [openSteps, setOpenSteps] = useState(() => new Map());
  // Profile lookup for "Marissa is chopping" / "Trevor is searing"
  // headers. Hydrated lazily from cook_session_participants.user_id.
  const [profilesById, setProfilesById] = useState(() => new Map());
  const [leadName, setLeadName] = useState(null);
  const [loading, setLoading] = useState(true);
  // Inline diagnostic state (temporary). When the empty-state banner
  // renders, we show what the query actually returned so we can tell
  // WHY there's no cook visible (no relationships? no active cooks?
  // stale bundle?). Remove this once the active-cook view is
  // confirmed working.
  const [diag, setDiag] = useState({ familyCount: null, activeCount: null, errFam: null, errSess: null });

  // ── Find any active cook by a family member ────────────────────
  // Previous behavior: required the kid to be explicitly added to
  // cook_session_participants. If a parent started cooking without
  // tagging the kid (which is the common case), the kid saw an
  // empty "Nothing cooking right now" state even though their
  // grown-up was cooking right next to them. Fix (2026-05-23):
  // walk family relationships → find any family member's active
  // cook_session and surface it. The kid no longer needs to be
  // invited as a participant.
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    const findActive = async () => {
      // Step 1: who's in this kid's family?
      const { data: rels, error: errFam } = await supabase
        .from("relationships")
        .select("requester_id, addressee_id")
        .eq("kind", "family")
        .eq("status", "accepted")
        .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
      if (cancelled) return;
      const familyIds = (rels || [])
        .map((r) => (r.requester_id === userId ? r.addressee_id : r.requester_id))
        .filter(Boolean);
      if (familyIds.length === 0) {
        setSession(null);
        setLoading(false);
        setDiag({ familyCount: 0, activeCount: 0, errFam: errFam?.message || null, errSess: null });
        return;
      }
      // Step 2: any active cook by a family member?
      const { data: rows, error: errSess } = await supabase
        .from("cook_sessions")
        .select("id, user_id, recipe_slug, recipe_title, recipe_emoji, status, started_at")
        .in("user_id", familyIds)
        .eq("status", "active")
        .order("started_at", { ascending: false });
      if (cancelled) return;
      setSession((rows || [])[0] || null);
      setLoading(false);
      setDiag({
        familyCount: familyIds.length,
        activeCount: (rows || []).length,
        errFam: errFam?.message || null,
        errSess: errSess?.message || null,
      });
    };
    findActive();

    // Realtime: any insert/update on cook_sessions for ANY user
    // means we may need to re-evaluate. Filter is "*" because
    // postgres_changes doesn't support IN-list filters; the
    // re-fetch above narrows back to family scope.
    const ch = supabase
      .channel(`rt:kidwatch:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_sessions" },
        () => findActive(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "relationships", filter: `requester_id=eq.${userId}` },
        () => findActive(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "relationships", filter: `addressee_id=eq.${userId}` },
        () => findActive(),
      )
      .subscribe();
    return () => {
      cancelled = true;
      try { supabase.removeChannel(ch); } catch {}
    };
  }, [userId]);

  // ── Fetch the lead's name + recipe + current step ─────────────
  useEffect(() => {
    if (!session?.id) {
      setRecipe(null);
      setLeadName(null);
      setOpenSteps(new Map());
      setProfilesById(new Map());
      return undefined;
    }
    let cancelled = false;

    (async () => {
      // Lead's name
      const { data: leadProfile } = await supabase
        .from("profiles").select("name, username").eq("id", session.user_id).maybeSingle();
      if (!cancelled) setLeadName(leadProfile?.name || leadProfile?.username || "Someone");

      // Recipe body (owned by the lead)
      const { data: ur } = await supabase
        .from("user_recipes").select("recipe").eq("user_id", session.user_id).eq("slug", session.recipe_slug).maybeSingle();
      if (cancelled) return;
      setRecipe(ur?.recipe || null);

      // Initial load — fetch EVERY open cook_session_steps row for
      // this session, not just the most recent. Multi-cook: Marissa
      // is chopping while Trevor is searing → both rows are open,
      // both should render as their own card.
      const { data: openRows } = await supabase
        .from("cook_session_steps")
        .select("id, user_id, step_id, step_title, started_at, finished_at, nominal_seconds, timer_state")
        .eq("cook_session_id", session.id)
        .is("finished_at", null)
        .order("started_at", { ascending: true });
      if (!cancelled) {
        const map = new Map();
        for (const r of (openRows || [])) map.set(r.id, r);
        setOpenSteps(map);
      }

      // Hydrate profiles for everyone with an open step (and the
      // lead) so we can label each card "Marissa is …" / "Trevor is …"
      const participantIds = new Set([session.user_id]);
      for (const r of (openRows || [])) {
        if (r.user_id) participantIds.add(r.user_id);
      }
      if (participantIds.size > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, name, username")
          .in("id", Array.from(participantIds));
        if (!cancelled && profs) {
          const pm = new Map();
          for (const p of profs) pm.set(p.id, p);
          setProfilesById(pm);
        }
      }
    })();

    // Realtime tail — patch the Map per row so unrelated cards
    // don't churn when one helper advances. Three cases:
    //   INSERT          → new open row, add card
    //   UPDATE (open)   → timer_state / step transition, refresh card
    //   UPDATE (closed) → row got finished_at, drop card
    //   DELETE          → row removed (rare), drop card
    const ch = supabase
      .channel(`rt:kidwatch-steps:${session.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps", filter: `cook_session_id=eq.${session.id}` },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row) return;
          if (payload.eventType === "DELETE") {
            setOpenSteps((prev) => {
              if (!prev.has(row.id)) return prev;
              const next = new Map(prev);
              next.delete(row.id);
              return next;
            });
            return;
          }
          if (row.finished_at) {
            setOpenSteps((prev) => {
              if (!prev.has(row.id)) return prev;
              const next = new Map(prev);
              next.delete(row.id);
              return next;
            });
            return;
          }
          // Open row — INSERT or UPDATE. Set/update the Map entry.
          setOpenSteps((prev) => {
            const next = new Map(prev);
            next.set(row.id, row);
            return next;
          });
          // Lazily hydrate profile for any new helper we haven't seen
          if (row.user_id && !profilesById.has(row.user_id)) {
            supabase
              .from("profiles")
              .select("id, name, username")
              .eq("id", row.user_id)
              .maybeSingle()
              .then(({ data }) => {
                if (!data || cancelled) return;
                setProfilesById((prev) => {
                  const next = new Map(prev);
                  next.set(data.id, data);
                  return next;
                });
              });
          }
        },
      )
      .subscribe();
    return () => { cancelled = true; try { supabase.removeChannel(ch); } catch {} };
  }, [session?.id, session?.user_id, session?.recipe_slug]);

  // Stable, ordered array of open step rows. Sort by started_at so
  // the cards stack in the order helpers picked them up. Map values
  // don't preserve insertion order across re-renders, so explicitly
  // sort.
  const openStepRows = useMemo(() => {
    const arr = Array.from(openSteps.values());
    arr.sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
    return arr;
  }, [openSteps]);

  // (Narration lives in useKidStepNarration on KidModeShell so it
  // fires from any tab, not just Watch. Don't duplicate here.)

  if (loading) {
    return (
      <PlaceholderShell emoji="🍳" hint="Looking for a cook to watch…" />
    );
  }

  if (!session) {
    return (
      <PlaceholderShell
        emoji="🌿"
        hint="Nothing cooking right now. When a grown-up starts cooking, you'll see it here!"
        diag={diag}
      />
    );
  }

  const recipeTitle = session.recipe_title || recipe?.title || "Cooking…";
  const recipeEmoji = session.recipe_emoji || recipe?.emoji || "🍽️";

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        padding: "16px 16px 40px",
        position: "relative",
      }}
    >
      {/* Lead + title hero */}
      <div
        style={{
          background: "linear-gradient(160deg, #FFE9C7 0%, #FFC988 100%)",
          borderRadius: 22,
          padding: "20px 18px",
          marginBottom: 16,
          boxShadow: "0 4px 20px rgba(212, 130, 30, 0.18)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 8 }}>{recipeEmoji}</div>
        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 11,
            letterSpacing: "0.14em",
            color: "rgba(0,0,0,0.55)",
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          {leadName?.toUpperCase()} IS COOKING
        </div>
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 26,
            color: COLOR.ink,
            lineHeight: 1.15,
            fontWeight: 600,
          }}
        >
          {recipeTitle}
        </div>
      </div>

      {/* Live step cards — one per helper actively running a step.
          When Marissa is chopping and Trevor is searing, this is two
          cards side-by-side. When everyone's between steps, the
          "waiting" placeholder shows. */}
      {openStepRows.length > 0 ? (
        openStepRows.map((row) => (
          <StepCard
            key={row.id}
            row={row}
            recipe={recipe}
            profile={row.user_id ? profilesById.get(row.user_id) : null}
            isLeadRow={row.user_id === session?.user_id}
          />
        ))
      ) : (
        <div
          style={{
            background: "#fff",
            border: `1.5px solid ${COLOR.border}`,
            borderRadius: 18,
            padding: "18px 18px 22px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: "0.14em",
              color: COLOR.sub,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            RIGHT NOW · LIVE
          </div>
          <div
            style={{
              fontFamily: FONT.detail,
              fontStyle: "italic",
              fontSize: 15,
              color: COLOR.muted,
              padding: "10px 0",
            }}
          >
            Waiting for the next step…
          </div>
        </div>
      )}

      {/* Rooting prompt */}
      <div
        style={{
          textAlign: "center",
          fontFamily: FONT.detail,
          fontStyle: "italic",
          fontSize: 15,
          color: COLOR.sub,
          padding: "0 16px",
        }}
      >
        Tap an emoji below to cheer them on!
      </div>

      {/* Reactions strip — kid is never the lead */}
      <ReactionsStrip cookSessionId={session.id} userId={userId} isLead={false} />
    </div>
  );
}

// StepCard — one helper's currently-open step. Owns its own tick
// loop so cards for different helpers don't interfere with each
// other's countdown. Renders the four phases the lead's timer can
// be in (READY / TICKING / PAUSED / ENDED) plus a header that says
// "<Helper> · Step N of M".
function StepCard({ row, recipe, profile, isLeadRow }) {
  // tick is the 1Hz re-render trigger. It's also threaded into the
  // displaySec/timerPhase computations BELOW (not useMemo'd against
  // tick deps) so the countdown actually progresses — without this,
  // setTick incremented but useMemo cached the first second's value
  // forever and the timer froze at its initial display value.
  const [tick, setTick] = useState(0);

  const stepDetail = useMemo(() => {
    if (!recipe?.steps?.length || !row?.step_id) return null;
    const target = String(row.step_id).replace(/-s\d+$/, "");
    return recipe.steps.find((s) => String(s.id) === target) || null;
  }, [recipe, row?.step_id]);

  const stepNumber = useMemo(() => {
    if (!recipe?.steps?.length || !row?.step_id) return null;
    const target = String(row.step_id).replace(/-s\d+$/, "");
    const idx = recipe.steps.findIndex((s) => String(s.id) === target);
    if (idx < 0) return null;
    return { index: idx + 1, total: recipe.steps.length };
  }, [recipe, row?.step_id]);

  const totalSec = row?.nominal_seconds ? Number(row.nominal_seconds) : null;
  const ts = row?.timer_state || null;
  const endsAtMs = ts?.endsAt
    ? new Date(ts.endsAt).getTime()
    : (row?.started_at && row?.nominal_seconds
        ? new Date(row.started_at).getTime() + Number(row.nominal_seconds) * 1000
        : null);

  const needsTick = ts?.endsAt && !ts?.isPaused && !ts?.ended;
  useEffect(() => {
    if (!needsTick) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [needsTick]);
  // Reference tick so the linter knows we use it (the count itself
  // doesn't matter — only the re-render it forces).
  void tick;

  // Phase + displaySec computed INLINE on every render (not memoed)
  // so Date.now() refreshes each tick. The cost is two cheap
  // computations per second per visible card; the benefit is the
  // countdown actually counts down.
  let timerPhase = "none";
  if (totalSec != null) {
    if (!ts) timerPhase = "ready";
    else if (ts.ended) timerPhase = "ended";
    else if (ts.isPaused) timerPhase = "paused";
    else if (!ts.endsAt) timerPhase = "ready";
    else {
      const remaining = Math.round((new Date(ts.endsAt).getTime() - Date.now()) / 1000);
      timerPhase = remaining <= 0 ? "ended" : "ticking";
    }
  }

  let displaySec = null;
  if (totalSec != null) {
    if (timerPhase === "ready") displaySec = totalSec;
    else if (timerPhase === "paused") displaySec = Math.max(0, Number(ts?.pausedRemainingSec) || 0);
    else if (timerPhase === "ended") displaySec = 0;
    else if (timerPhase === "ticking" && endsAtMs) {
      displaySec = Math.max(0, Math.round((endsAtMs - Date.now()) / 1000));
    }
  }

  const progressPct = (totalSec && displaySec != null)
    ? Math.max(0, Math.min(100, Math.round(100 * (1 - displaySec / totalSec))))
    : null;
  const fmtMMSS = (s) => {
    if (s == null) return "—";
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  // Header label — "MARISSA · CHOPPING" or "TREVOR · COOKING". Falls
  // back to "SOMEONE" when we haven't hydrated the profile yet (the
  // realtime tail handles late-arriving profile data).
  const helperName = profile?.name?.split(/\s+/)[0]
    || profile?.username
    || (isLeadRow ? "GROWN-UP" : "SOMEONE");

  return (
    <div
      style={{
        background: "#fff",
        border: `1.5px solid ${COLOR.border}`,
        borderRadius: 18,
        padding: "18px 18px 22px",
        marginBottom: 16,
      }}
    >
      <div style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: 6,
      }}>
        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: "0.14em",
            color: COLOR.sub,
            fontWeight: 700,
          }}
        >
          {helperName.toUpperCase()} · LIVE
        </div>
        {stepNumber && (
          <div style={{
            fontFamily: FONT.mono,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.14em",
            color: COLOR.sub,
          }}>
            STEP {stepNumber.index} OF {stepNumber.total}
          </div>
        )}
      </div>

      {stepDetail ? (
        <>
          <div style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 22,
            color: COLOR.ink,
            lineHeight: 1.2,
            marginBottom: 8,
          }}>
            {row?.step_title || stepDetail.title || `Step ${stepDetail.id}`}
          </div>
          {stepDetail.instruction && (
            <div style={{
              fontFamily: FONT.detail,
              fontSize: 14,
              lineHeight: 1.5,
              color: COLOR.muted,
              marginBottom: displaySec != null ? 14 : 0,
            }}>
              {stepDetail.instruction}
            </div>
          )}

          {displaySec != null && (
            <div style={{ marginTop: 4 }}>
              <div style={{
                display: "flex", alignItems: "baseline",
                justifyContent: "space-between", gap: 12, marginBottom: 8,
              }}>
                <div style={{
                  fontFamily: FONT.mono,
                  fontSize: 38,
                  fontWeight: 700,
                  color: timerPhase === "ended"  ? COLOR.leaf
                       : timerPhase === "paused" ? COLOR.sub
                       : timerPhase === "ready"  ? COLOR.sub
                       : COLOR.ink,
                  letterSpacing: "0.02em",
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {fmtMMSS(displaySec)}
                </div>
                <div style={{
                  fontFamily: FONT.mono,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: timerPhase === "ended"  ? COLOR.leaf
                       : timerPhase === "paused" ? "#C28F2C"
                       : timerPhase === "ready"  ? COLOR.sub
                       : COLOR.sub,
                }}>
                  {timerPhase === "ended"  ? "TIMER DONE!"
                   : timerPhase === "paused" ? "PAUSED"
                   : timerPhase === "ready"  ? "WAITING TO START"
                   : "MIN LEFT"}
                </div>
              </div>
              {progressPct != null && (
                <div style={{
                  height: 10,
                  background: "rgba(0,0,0,0.06)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${progressPct}%`,
                    height: "100%",
                    background: timerPhase === "ended"
                      ? COLOR.leaf
                      : timerPhase === "paused"
                        ? "rgba(194, 143, 44, 0.5)"
                        : timerPhase === "ready"
                          ? "rgba(0,0,0,0.18)"
                          : "linear-gradient(90deg, #F0A847 0%, #E07C2F 100%)",
                    transition: "width 0.6s linear",
                  }} />
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div style={{
          fontFamily: FONT.detail,
          fontStyle: "italic",
          fontSize: 15,
          color: COLOR.muted,
          padding: "10px 0",
        }}>
          {row?.step_title || "Working on a step…"}
        </div>
      )}
    </div>
  );
}

function PlaceholderShell({ emoji, hint, diag }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 32px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 80, lineHeight: 1, marginBottom: 20, opacity: 0.85 }}>{emoji}</div>
      <div
        style={{
          fontFamily: FONT.detail,
          fontStyle: "italic",
          fontSize: 18,
          color: COLOR.muted,
          lineHeight: 1.4,
          maxWidth: 320,
        }}
      >
        {hint}
      </div>
      {/* Temporary inline diagnostic — surfaces what the lookup
          actually returned so we can tell why the empty state is
          showing. If diag.familyCount === null, the new build
          hasn't reached the device yet (this whole block won't
          render at all in that case). Remove once confirmed
          working. */}
      {diag && (diag.familyCount !== null || diag.activeCount !== null) && (
        <div
          style={{
            marginTop: 24,
            padding: "12px 16px",
            background: "rgba(0,0,0,0.05)",
            border: `1px dashed ${COLOR.border}`,
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 12,
            color: COLOR.ink,
            maxWidth: 360,
            textAlign: "left",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>DIAG (build 2026-05-23):</div>
          <div>family members found: <b>{diag.familyCount}</b></div>
          <div>active cooks for them: <b>{diag.activeCount}</b></div>
          {diag.errFam && <div style={{ color: COLOR.rose }}>relationships err: {diag.errFam}</div>}
          {diag.errSess && <div style={{ color: COLOR.rose }}>cook_sessions err: {diag.errSess}</div>}
        </div>
      )}
    </div>
  );
}

export default KidWatchTab;
